<template>
  <div class="settings-container step-one">
    <div class="retailers-container">
      <div class="retailers-wrapper">
        <search-bar clearSearch
                    isDebounce
                    sectionType="retailers"
                    @getSearchResult="getSearchResult">
        </search-bar>
        <div class="retailer-lists-wrapper">
          <div class="left-half">
            <div class="sortAndFilter-wrapper">
              <dropdown :context="sortData.context"
                        :defaultOption="sortData.defaultOption"
                        :hidden="sortData.hidden"
                        :optionClasses="sortData.optionClasses"
                        :options="sortData.options"
                        :showCheckbox="sortData.showCheckbox"
                        @reloadData="reloadRetailersList">
              </dropdown>
              <dropdown ref="dropdownWrapper"
                        :context="multiSelectData.context"
                        :defaultOption="multiSelectData.defaultOption"
                        :hidden="multiSelectData.hidden"
                        :optionClasses="multiSelectData.optionClasses"
                        :options="multiSelectData.options"
                        :showCheckbox="multiSelectData.showCheckbox"
                        @reloadData="reloadRetailersList">
              </dropdown>
            </div>
            <retailers-list
              class="left-list"
              ref="normalRetailersList"
              listKey="merchants"
              errorTerm="retailers"
              :currentAdvertiserId="currentAdvertiserId"
              :dataBreakdown="breakdownRetailer"
              :listUrl="`/merchants?brand_id=${currentAdvertiserId}`"
              :searchText="searchText"
              :selectAll.sync="selectAll"
              :selectedItemIds="selectedRetailerIds"
              @clearRetailers="clearRetailers"
              @setRetailerErrors="setRetailerErrors"
              @setSelectedRetailerIds="setSelectedRetailerIds"
              @showClearAllModal="showClearAllModal"
              hasSelectAll
              useFiltersAndSort
              showEmptyList
              >
            </retailers-list>
          </div>
          <div class="right-half">
            <retailers-list
              class="right-list"
              ref="sponsorRetailersList"
              listKey="sponsored_requests"
              errorTerm="sponsor requests"
              :currentAdvertiserId="currentAdvertiserId"
              :dataBreakdown="breakdownSponsorRequest"
              :listTitle="'merchant sponsor request'"
              :listUrl="`/marketplace_sponsor_requests?brand_id=${currentAdvertiserId}`"
              :searchText="searchText"
              :selectedItemIds="merchantSponsorRequestId"
              @setRetailerErrors="setRetailerErrors"
              @setSelectedRetailerIds="setSelectedRetailerIds"
            />
          </div>
         </div>

        <modal
          v-if="clearAllModal"
          buttonText="clear"
          showClose
          type="warning"
          wrapperClasses="warning-modal"
          @close="clearAllModal = false"
          @proceed="clearRetailers">
          <div slot="header">
            <h3 class="sub-title">clear all selected retailers</h3>
          </div>
          <div slot="body">
            <p>are you sure you want to clear all retailers selected in step 1?</p>
          </div>
        </modal>
      </div>
      <retailers-map
        :currentAdvertiserId="currentAdvertiserId"
        :selectedRetailerIds="selectedRetailerIds"
        @setRegionsForFilter="setRegionsForFilter"
        @setSelectedRetailerIds="setSelectedRetailerIds" />
    </div>
  </div>
</template>

<script>
  import config from '../../config'
  import Dropdown from '../Shared/Dropdown'
  import RetailersList from '../Ads/RetailersList'
  import RetailersMap from '../Map/RetailersMap'
  import SearchBar from '../Shared/SearchBar'
  import Modal from '../Shared/Modal'
  import { format } from 'date-fns'
  import { NumberFormatters } from '../mixins/NumberFormatters.js'

  export default {
    props: {
      currentAdvertiserId: [Number, String],
      selectedRetailerIds: Array,
      merchantSponsorRequestId: Array
    },

    name: 'StepOne',

    mixins: [NumberFormatters],

    components: {
      Dropdown,
      RetailersList,
      RetailersMap,
      SearchBar,
      Modal
    },

    data() {
      return {
        sortData: {
          context: 'sort by',
          defaultOption: 'a to z',
          optionClasses: 'sort-items',
          showCheckbox: false,
          options: [
            {
              name: 'a to z',
              value: 'q[s][]=name asc'
            },
            {
              name: 'z to a',
              value: 'q[s][]=name desc'
            },
            {
              name: 'largest customer list',
              value: 'q[s][]=cached_allowed_sms_count desc'
            },
            {
              name: 'smallest customer list',
              value: 'q[s][]=cached_allowed_sms_count asc'
            }
          ],
          hidden: false
        },

        multiSelectData:  {
          context: 'state/prov',
          showCheckbox: true,
          defaultOption: 'all',
          optionClasses: 'filter-items',
          options: null,
          hidden: true
        },
        retailerIds: null,
        clearAllModal: false,
        selectAll: null,
        searchText: ""
      }
    },

    methods: {
      breakdownRetailer(retailer) {
        return {
          id: retailer.id,
          detail: retailer.name,
          subDetail: `${this.abbrNum(retailer.recipient_sms_size, 1)} consumers`
        }
      },

      breakdownSponsorRequest(request) {
        return {
          id: request.id,
          detail: request.merchant_name,
          subDetail: this.formatDate(request.last_sponsor_request),
          extra: request.merchant_id,
          isSponsorRequest: true,
          request_log: request.request_log,
          sponsor_types: request.sponsor_types,
          hasDetails: !!request?.request_log,
          image: request.image
        }
      },

      formatDate(sponsoredDate) {
        return format(new Date(sponsoredDate), 'MM/dd/yy')
      },

      removeHidden() {
        this.$set(this.multiSelectData, 'hidden', false)
      },

      setRegionsForFilter(merchantRegions) {
        if (merchantRegions?.length > 1) {
          merchantRegions = merchantRegions.sort()
          this.removeHidden()
          this.$set(this.multiSelectData, 'options', merchantRegions)
        }
      },

      reloadRetailersList() {
        this.$refs.normalRetailersList.changePage(1)
      },

      setRetailerErrors(message) {
        this.$emit('setPageErrors', 'retailersApi', message)
      },

      getMultiSelectList() {
        return this.$refs.dropdownWrapper.getMultiSelect()
      },

      getSearchResult(section, text) {
        this.searchText = text
      },

      setSelectedRetailerIds(selectedItem, retailerIds, additionalParams) {
        let params
        if (selectedItem?.isSponsorRequest) {
          this.clearAllModal = false
          this.selectAll = false
          const sponsorRequestId = retailerIds?.pop()
          params = {
            marketplace_sponsor_request_id: sponsorRequestId || null,
            retailers: sponsorRequestId ? [selectedItem.extra] : []
          }
        } else {
          params = {
            marketplace_sponsor_request_id: null,
            retailers: retailerIds
          }
        }
        this.$emit('updateAd', params, additionalParams)
      },

      showClearAllModal() {
        this.clearAllModal = true
      },

      clearRetailers()  {
        this.clearAllModal = false
        this.selectAll = false
        this.setSelectedRetailerIds(null, [])
      },
    },

    created() {
      this.retailerIds = this.selectedRetailerIds
    },

    mounted() {
      this.$set(this.multiSelectData, 'hidden', true)
    },

    watch: {
      selectedRetailerIds() {
        this.retailerIds = this.selectedRetailerIds
      },
    },
  }
</script>

<style lang="sass">
  @import '../../assets/sass/colors'

  .settings-container.step-one.disabled
    opacity: .5
    pointer-events: none

  .hidden
    display: none

  .retailers-container
    display: flex
    flex: 1
    justify-content: space-between
    margin: 0 2rem
    @media only screen and (max-width: 40em)
      flex-wrap: wrap

    .retailers-wrapper
      display: flex
      flex-flow: column
      width: 55%
      @media only screen and (max-width: 40em)
        width: 100%

  .recipients-wrapper
    align-items: flex-start
    display: inline-flex
    flex-direction: column
    margin: 5rem auto
    @media only screen and (max-width: 40em)
      margin: 2rem auto

  .retailers-list-wrapper .retailers-list,
  .retailers-map-section
    min-height: 450px
    max-height: 490px
    @media only screen and (max-width: 64em)
      min-height: auto

  .search-bar-container
    text-align: left
    max-width: 685px

    .search-bar
      border-radius: 32px
      width: 80%

  .sortAndFilter-wrapper
    margin-bottom: 20px
    display: grid
    grid-template-columns: repeat(2, 1fr)
    @media only screen and (max-width: 1300px)
      grid-template-columns: 1fr
      .dropdown-wrapper
        margin-bottom: 1em

  .modal
    padding: 30px
    border-radius: 10px
    max-width: 355px

    p
      margin: 20px 0

    .sub-title
      font-weight: bold
      margin-bottom: 10px
      padding: 0

    .modal-close
      top: 2px
      left: 3px

  .retailer-lists-wrapper
    display: flex
    .left-half
      display: flex
      flex-grow: 2
      flex-direction: column
    .right-half
      display: flex
      flex-grow: 1
      justify-content: center
    @media only screen and (max-width: 950px)
      flex-direction: column-reverse

  .right-list
    text-align: left
    .merchant-wrapper .check-row
      cursor: pointer
    p
      margin-top: 20px
      color: $primary-color
    .item-info span:last-child
      font-size: small

  .left-list
    .retailers-list
      display: grid
      grid-template: repeat(5,1fr) / repeat(2,1fr)
      grid-auto-flow: column
</style>
